import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand-lockup${compact ? " brand-lockup-compact" : ""}`}>
      <span className="brand-mark" aria-hidden="true">N</span>
      <span>Nettstay</span>
    </span>
  );
}

export function AuthLayout({
  children,
  eyebrow,
  title,
  description,
}: {
  children: ReactNode;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <main className="auth-shell">
      <section className="auth-brand-panel" aria-label="About Nettstay">
        <a className="auth-brand-link" href="https://nettstay.co.nz" aria-label="Nettstay home">
          <BrandMark />
        </a>
        <div className="auth-brand-copy">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <ul className="auth-value-list">
          <li><span aria-hidden="true">01</span><div><strong>One clear financial view</strong><p>See property, income, expense and tax information together.</p></div></li>
          <li><span aria-hidden="true">02</span><div><strong>Built for New Zealand hosts</strong><p>Calm, practical workflows for rental property owners.</p></div></li>
          <li><span aria-hidden="true">03</span><div><strong>Your data stays protected</strong><p>Secure sessions and a dedicated API boundary by design.</p></div></li>
        </ul>
        <p className="auth-brand-footnote">Property finances, made easier to understand.</p>
      </section>
      <section className="auth-form-panel">
        <div className="auth-mobile-brand"><BrandMark compact /></div>
        <div className="auth-card">{children}</div>
        <p className="auth-legal">By continuing, you agree to use Nettstay responsibly and keep your account secure.</p>
      </section>
    </main>
  );
}

export function Card({
  children,
  title,
  description,
  className = "",
}: {
  children: ReactNode;
  title?: string;
  description?: string;
  className?: string;
}) {
  return (
    <section className={`card ${className}`.trim()}>
      {(title || description) && (
        <div className="card-heading">
          {title && <h2>{title}</h2>}
          {description && <p>{description}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "quiet";
}) {
  return <button className={`btn btn-${variant} ${className}`.trim()} {...rest}>{children}</button>;
}

export function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {error && <span className="field-error" role="alert">{error}</span>}
    </label>
  );
}

export function TextInput({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`input ${className}`.trim()} {...props} />;
}

export function Select({ className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`input select-input ${className}`.trim()} {...props} />;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="spinner-ring" aria-hidden="true" />
      <span>{label ?? "Loading…"}</span>
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title?: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-state-mark" aria-hidden="true">N</span>
      <div>
        <strong>{title ?? "Nothing here yet"}</strong>
        <p>{message}</p>
      </div>
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="error-banner" role="alert">
      <strong>We couldn&apos;t complete that request.</strong>
      <span>{message}</span>
    </div>
  );
}

export function InfoBanner({ message }: { message: string }) {
  return <div className="info-banner" role="status">{message}</div>;
}

const supportedCurrencies = new Set(Intl.supportedValuesOf("currency"));

export function formatMoney(amount: number, currency: string | null | undefined): string {
  const normalizedCurrency = currency?.trim().toUpperCase();
  if (!normalizedCurrency || !supportedCurrencies.has(normalizedCurrency)) {
    return new Intl.NumberFormat("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }
  try {
    return new Intl.NumberFormat("en-NZ", { style: "currency", currency: normalizedCurrency }).format(amount);
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    return new Intl.NumberFormat("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }
}

export function Money({ amount, currency }: { amount: number; currency?: string | null }) {
  return <span className="money">{formatMoney(amount, currency)}</span>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "default" | "positive" | "attention";
}) {
  return (
    <div className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint && <p>{hint}</p>}
    </div>
  );
}

function titleCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function renderDetailValue(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") return <span className="muted">Not available</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="muted">None</span>;
    return value.map((item) => typeof item === "object" ? JSON.stringify(item) : String(item)).join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${titleCase(key)}: ${typeof item === "object" ? JSON.stringify(item) : String(item)}`)
      .join(" · ");
  }
  return String(value);
}

export function DetailList({ data }: { data: unknown }) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return <p>{renderDetailValue(data)}</p>;
  }
  return (
    <dl className="detail-list">
      {Object.entries(data as Record<string, unknown>).map(([key, value]) => (
        <div key={key}>
          <dt>{titleCase(key)}</dt>
          <dd>{renderDetailValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
